import React, { MouseEventHandler, useState } from 'react';
import { fromJS } from 'immutable';
import { twMerge } from 'tailwind-merge';

import CallAdaptiveButton from 'flow/components/CallAdaptiveButton';
import PbxQueueContextMenu from 'flow/components/context-menus/PbxQueueContextMenu';
import ContextMenu, { LockScroll } from 'flow/components/ContextMenu';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppSelector } from 'flow/hooks/useRedux';
import {
    selectQueueActiveProfile,
    selectQueueDescription,
    selectQueueIsOfTypeCentionOmni,
    selectQueuePhoneNumber,
} from 'flow/selectors/queue-selectors';
import { QueueKey } from 'flow/types/EntityKeys';
import { extractStatusText, getActiveUntil } from 'flow/utils';
import AgentsLoggedInToQueueCount from './AgentsLoggedInToQueueCount';
import CallsInQueueCount from './CallsInQueueCount';
import PbxListItem from './PbxListItem';
import { getMoreOptionsLabel } from 'flow/utils/a11y-utils';
import { RowMarking, rowMarking } from './BaseListItem';

interface Props {
    itemKey: QueueKey;
    onClick: MouseEventHandler;
    lockScroll: LockScroll;
    marking?: RowMarking;
    enableCallButton?: boolean;
}

const ListItemPbxQueue = ({
    itemKey,
    onClick,
    lockScroll,
    marking,
    enableCallButton,
}: Props) => {
    const { i18next } = useAppContext();
    const [contextMenuIsActive, setContextMenuIsActive] = useState(false);

    const activeProfile = useAppSelector((state) =>
        selectQueueActiveProfile(state, itemKey),
    );

    const queueIsTypeCentionOmni = useAppSelector((state) =>
        selectQueueIsOfTypeCentionOmni(state, itemKey),
    );

    const queuePhoneNumber = useAppSelector((state) =>
        selectQueuePhoneNumber(state, itemKey),
    );

    const description = useAppSelector((state) =>
        selectQueueDescription(state, itemKey),
    );

    const showCallButton =
        activeProfile?.get('available') && enableCallButton && queuePhoneNumber;

    return (
        <PbxListItem
            onClick={onClick}
            marking={
                marking ?? rowMarking({ highlighted: contextMenuIsActive })
            }
            type={queueIsTypeCentionOmni ? 'omniQueue' : 'queue'}
            title={description}
            dataTestId={`pbx-list-item-${itemKey}`}
            description={
                activeProfile &&
                `${extractStatusText(
                    activeProfile,
                    i18next,
                )} ${getActiveUntil(activeProfile, i18next)}`
            }
        >
            {
                <>
                    <div
                        className={twMerge(
                            'flex gap-2',
                            contextMenuIsActive
                                ? 'hidden'
                                : 'group-focus-visible-within:hidden group-hover:hidden',
                        )}
                    >
                        <CallsInQueueCount queueKey={itemKey} />
                        {activeProfile?.get('available') ? (
                            <AgentsLoggedInToQueueCount
                                queueKey={itemKey}
                                isSelected={marking !== undefined}
                            />
                        ) : null}
                    </div>

                    <div
                        className={twMerge(
                            'gap-x-2',
                            contextMenuIsActive
                                ? 'flex'
                                : 'sr-only-until-hover-or-focus flex',
                        )}
                    >
                        {itemKey && (
                            <ContextMenu
                                triggerLabel={getMoreOptionsLabel(
                                    i18next,
                                    description,
                                )}
                                preventOverflow={true}
                                setActive={setContextMenuIsActive}
                                lockScroll={lockScroll}
                            >
                                <PbxQueueContextMenu queueKey={itemKey} />
                            </ContextMenu>
                        )}
                        {showCallButton && (
                            <CallAdaptiveButton
                                phoneNumber={queuePhoneNumber}
                                transferContact={fromJS({
                                    description: description,
                                    key: itemKey,
                                })}
                                activeBg={true}
                            />
                        )}
                    </div>
                </>
            }
        </PbxListItem>
    );
};

export default ListItemPbxQueue;
